import { motion } from 'framer-motion';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  viewport: { once: true, margin: '-100px' },
};

const AboutPage = () => {
  const leadership = [
    {
      name: 'Jatinder Singh',
      title: 'Managing Director',
      bio: 'Jatinder Singh, Founder and Managing Director, brings a quarter-century of engineering precision and strategic insight to Quality Services. Holding an MBA in HR, he has cultivated a company culture rooted in excellence and service. His leadership ensures that we not only deliver superior technology but also understand the unique human needs of every business we serve.',
      image: process.env.PUBLIC_URL + '/images/leader_jatinder.webp',
    },
    {
      name: 'Harpreet Singh',
      title: 'Executive Director',
      bio: 'Strategic thinker driving innovation and customer excellence. Spearheads business development and ensures the highest standards of service delivery.',
      image: process.env.PUBLIC_URL + '/images/leader_harpreet.webp',
    },
  ];

  return (
    <div data-testid="about-page" className="pt-32 pb-24 bg-[#F2F0E9]">
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <motion.div {...fadeInUp} className="text-center mb-24">
          <h1 className="hero-title text-[#1A1A1A] mb-6 accent-cyan">
            Our Story
          </h1>
          <p className="text-lg md:text-xl text-[#595959] max-w-3xl mx-auto leading-relaxed">
            A legacy of excellence spanning over two decades.
          </p>
        </motion.div>

        {/* Company Story */}
        <motion.div {...fadeInUp} data-testid="company-story" className="mb-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="aspect-[4/5] overflow-hidden bg-white">
                <img
                  loading="lazy"
                  decoding="async"
                  src={process.env.PUBLIC_URL + '/images/hero_bg.webp'}
                  alt="Quality Services Office"
                  width="800"
                  height="1000"
                  className="w-full h-full object-cover img-desaturate"
                />
              </div>
            </div>

            <div className="space-y-6">
              <p className="cinzel text-sm uppercase tracking-widest text-[#595959]">
                Since 1998
              </p>
              <h2 className="serif text-3xl md:text-4xl text-[#1A1A1A]">
                Building a Legacy of Excellence
              </h2>
              <p className="text-base text-[#595959] leading-relaxed">
                Quality Services was founded with a singular vision: to bring world-class printing and document management solutions to businesses across the region.
              </p>
              <p className="text-base text-[#595959] leading-relaxed">
                As an authorized Xerox franchise, we've grown from a small operation to a trusted partner for hundreds of organizations, from educational institutions to corporate enterprises.
              </p>
              <p className="text-base text-[#595959] leading-relaxed">
                Our success is built on unwavering commitment to quality, personalized service, and deep understanding of our clients' needs. Every solution we provide is tailored to deliver maximum value and efficiency.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Leadership Section */}
        <motion.div {...fadeInUp} data-testid="leadership-section">
          <h2 className="section-title text-center text-[#1A1A1A] mb-16 accent-magenta">
            Leadership
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
            {leadership.map((leader, index) => (
              <motion.div
                key={index}
                data-testid={`leader-profile-${index}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true }}
              >
                <div className="aspect-[3/4] overflow-hidden bg-white mb-8">
                  <img
                    loading="lazy"
                    decoding="async"
                    src={leader.image}
                    alt={leader.name}
                    width="600"
                    height="800"
                    className="w-full h-full object-cover img-desaturate"
                  />
                </div>
                <h3 className="serif text-2xl text-[#1A1A1A] mb-2">{leader.name}</h3>
                <p className="text-xs uppercase tracking-widest text-[#595959] mb-4">
                  {leader.title}
                </p>
                <p className="text-base text-[#595959] leading-relaxed">{leader.bio}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Mission Statement */}
        <motion.div
          {...fadeInUp}
          data-testid="mission-statement"
          className="mt-32 bg-[#EBE8E1] py-20 px-6 md:px-12 text-center"
        >
          <h2 className="serif text-3xl md:text-4xl text-[#1A1A1A] mb-8">
            Our Mission
          </h2>
          <p className="text-lg text-[#595959] max-w-4xl mx-auto leading-relaxed">
            To transform the modern enterprise and revolutionize educational environments through the seamless integration of next-generation technology.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default AboutPage;
